import React, { useState, useEffect } from 'react';
import { AllowlistEntry } from '../../types';
import { settingsService } from '../../services/api';
import { formatDateTime } from '../../utils/datetime';

const AllowListPanel: React.FC = () => {
  const [entries, setEntries] = useState<AllowlistEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newCidr, setNewCidr] = useState('');
  const [newDescription, setNewDescription] = useState('');

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editDescription, setEditDescription] = useState('');

  const fetchEntries = async () => {
    setLoading(true);
    setError(null);
    try {
      setEntries(await settingsService.listAllowlist());
    } catch (err) {
      setError('Failed to fetch allowlist');
      console.error('Error fetching allowlist:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEntries();
  }, []);

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await settingsService.addAllowlistEntry(newCidr.trim(), newDescription.trim());
      setNewCidr('');
      setNewDescription('');
      fetchEntries();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to add entry');
    }
  };

  const handleDelete = async (id: number) => {
    setError(null);
    try {
      await settingsService.deleteAllowlistEntry(id);
      fetchEntries();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to delete entry');
    }
  };

  const startEditing = (entry: AllowlistEntry) => {
    setEditingId(entry.id);
    setEditDescription(entry.description || '');
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditDescription('');
  };

  const saveEditing = async (id: number) => {
    setError(null);
    try {
      await settingsService.updateAllowlistEntry(id, editDescription.trim());
      cancelEditing();
      fetchEntries();
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to update entry');
    }
  };

  if (loading) return <div className="loading">Loading allowlist...</div>;

  return (
    <div className="settings-panel">
      <h3>SIP/RTP source-IP allowlist</h3>
      <p className="settings-hint">
        Default is deny — only IPs/CIDRs listed here may reach SIP or RTP. Changes take effect on the
        recorder within its refresh interval (default 30s).
      </p>
      {error && <div className="error">{error}</div>}

      <div className="table-container">
        <table className="sessions-table">
          <thead>
            <tr>
              <th>CIDR</th>
              <th>Description</th>
              <th>Added by</th>
              <th>Added at</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {entries.length === 0 ? (
              <tr><td colSpan={5} className="no-data">No entries — everything is currently blocked</td></tr>
            ) : (
              entries.map(entry => (
                <tr key={entry.id}>
                  <td className="monospace">{entry.cidr}</td>
                  <td>
                    {editingId === entry.id ? (
                      <input
                        type="text"
                        value={editDescription}
                        onChange={e => setEditDescription(e.target.value)}
                        placeholder="Description"
                      />
                    ) : (
                      entry.description || ''
                    )}
                  </td>
                  <td className="monospace small">{entry.created_by || ''}</td>
                  <td>{formatDateTime(entry.created_at)}</td>
                  <td>
                    {editingId === entry.id ? (
                      <>
                        <button type="button" onClick={() => saveEditing(entry.id)}>Save</button>
                        <button type="button" onClick={cancelEditing}>Cancel</button>
                      </>
                    ) : (
                      <>
                        <button type="button" onClick={() => startEditing(entry)}>Edit</button>
                        <button type="button" onClick={() => handleDelete(entry.id)}>Remove</button>
                      </>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <form onSubmit={handleAdd} className="search-form settings-form">
        <input
          type="text"
          placeholder="IP or CIDR, e.g. 203.0.113.5 or 203.0.113.0/24"
          value={newCidr}
          onChange={e => setNewCidr(e.target.value)}
          required
        />
        <input
          type="text"
          placeholder="Description (optional)"
          value={newDescription}
          onChange={e => setNewDescription(e.target.value)}
        />
        <button type="submit">Add</button>
      </form>
    </div>
  );
};

export default AllowListPanel;
